/* eslint-disable no-restricted-imports -- this wrapper is the single canonical entry point for dayjs; all consumers must import from `@common/services/datetime`. */
import dayjs, { type Dayjs as DatetimeType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);

// Matches a trailing UTC offset in an ISO 8601 string: `+05:00`, `-0530`, `+08`.
// Groups: 1 = sign, 2 = hours, 3 = minutes (optional).
const ISO_OFFSET_SUFFIX = /([+-])(\d{2}):?(\d{2})?$/;

export function parseZone(input: string | DatetimeType): DatetimeType {
  if (typeof input !== 'string') {
    return input;
  }

  if (input.endsWith('Z')) {
    return dayjs.utc(input);
  }

  const match = input.match(ISO_OFFSET_SUFFIX);
  if (!match) {
    return dayjs(input);
  }

  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] || '0', 10);
  const totalMinutes = sign * (hours * 60 + minutes);

  return dayjs(input).utcOffset(totalMinutes);
}

export function isDatetime(value: unknown): value is DatetimeType {
  return dayjs.isDayjs(value);
}

export const datetime = dayjs;
export type { DatetimeType };
